import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Translate, { translate } from '@securitize/ui-components/i18n/Translate';
import {
  Dropdown, DropdownItem, DropdownMenu, DropdownToggle,
} from 'reactstrap';
import { headerThemes } from '../../utils/constants';

import './UserMenu.scss';

const UserMenu = ({
  hideUserData,
  menuItems,
  logo,
  theme,
}) => {
  const history = useHistory();
  const {
    investorFullName, investorEmail, verificationStatus, investorType, entityName,
  } = useSelector(({ auth }) => auth.user);
  const [isMenu, setMenuState] = useState(false);

  const hasFullName = !!investorFullName.replaceAll('-', '').trim();
  const displayName = investorType === 'entity' && entityName
    ? entityName
    : hasFullName ? investorFullName : investorEmail;

  const statusIcon = verificationStatus === 'verified' ? 'check'
    : verificationStatus === 'service-denied' ? 'error' : 'clock';

  return (
    <>
      <div className="user-menu-nie font-16">
        <div
          className={classNames('user-menu-nie_logo', {
            'user-menu-nie_logo--align': hideUserData,
          })}
        >
          {logo && (
            <img
              src={logo.src}
              className={`user-menu-nie_logo__${logo.className}`}
              alt={translate(logo.alt)}
            />
          )}
        </div>
        {
          !hideUserData && (
            <Dropdown
              isOpen={isMenu}
              toggle={() => setMenuState(!isMenu)}
              className="d-flex flex-column align-items-end"
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="user-menu-nie_data">
                  <div className="font-16 text-truncate text-right">{displayName}</div>
                  <div className="font-16 text-truncate text-right mt-2">
                    <span
                      className={classNames(`user-menu-nie_menu__status--${verificationStatus}`)}
                    >
                      <Translate translationKey={`Texts.${verificationStatus}`} />
                    </span>
                  </div>
                </div>
                <DropdownToggle
                  onClick={() => setMenuState(!isMenu)}
                // issue with eslint
                  onKeyPress={() => {
                  }}
                  className={classNames('user-menu-nie_avatar font-16', {
                    fill1: ['pending', 'not-verified', 'updates-required'].includes(verificationStatus),
                    fill2: verificationStatus === 'service-denied',
                    fill3: verificationStatus === 'verified',
                    'user-menu-nie_avatar__dark': theme === headerThemes.DARK,
                  })}
                  tag="button"
                  tabIndex="0"
                >
                  <i className={investorType === 'entity' ? 'icon-entity' : 'icon-profile'} />
                </DropdownToggle>
              </div>
              <DropdownMenu
                className={classNames('user-menu-nie_menu font-16 border-0', {
                  active: isMenu,
                })}
              >
                <div>
                  {hasFullName && (<div className="font-16 text-truncate mt-2">{displayName}</div>)}
                  <div className="font-16 subtext text-truncate mt-2">{investorEmail}</div>
                  <div className="font-16 text-truncate mt-2">
                    <span>
                      <Translate translationKey="Texts.status" />
                      <span>: &nbsp;</span>
                    </span>
                    <span
                      className={classNames(`user-menu-nie_menu__status--${verificationStatus}`)}
                    >
                      <i className={`nie-icon icon-${statusIcon} mr-1`} />
                      <Translate translationKey={`Texts.${verificationStatus}`} />
                    </span>
                  </div>
                </div>
                <hr />
                {menuItems.map(({
                  path, icon, onClick, i18nKey, isExternalUrl,
                }) => (
                  <DropdownItem
                    key={path}
                    onClick={() => {
                      if (onClick)onClick();
                      setMenuState(!isMenu);
                      if (isExternalUrl) window.open(path);
                      else history.push(path);
                    }}
                    className="user-menu-nie_menu__item h-100"
                  >
                    <i className={`${icon} font-24 mr-3`} />
                    <span>
                      <Translate translationKey={i18nKey} />
                    </span>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )
        }
      </div>
    </>
  );
};

UserMenu.defaultProps = {
  hideUserData: false,
  logo: null,
  theme: headerThemes.LIGHT,
};

UserMenu.propTypes = {
  hideUserData: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    path: PropTypes.string.isRequired,
    i18nKey: PropTypes.string.isRequired,
    isExternalUrl: PropTypes.bool,
  })).isRequired,
  logo: PropTypes.shape({
    src: PropTypes.node.isRequired,
    alt: PropTypes.string.isRequired,
    className: PropTypes.string.isRequired,
  }),
  theme: PropTypes.oneOf(Object.keys(headerThemes)),
};

export default UserMenu;
